import { z } from 'zod'
import { idSchema, isoDateTimeSchema, optionalText, text } from './common.js'

export type InspectionTone = 'success' | 'warning' | 'error' | 'neutral'

export interface InspectionScaleOption {
  value: string
  label: string
  tone: InspectionTone
}

/**
 * The rating scales a checklist item can use. A workshop picks one of these
 * per item when editing the checklist in Settings — there's no free-form
 * custom scale, just a choice among a plain condition rating, a severity
 * call, or a yes/no ("checkbox") item.
 */
export const INSPECTION_SCALES = {
  condition: [
    { value: 'good', label: 'Good', tone: 'success' },
    { value: 'fair', label: 'Fair', tone: 'warning' },
    { value: 'poor', label: 'Poor', tone: 'error' },
    { value: 'na', label: 'N/A', tone: 'neutral' },
  ],
  severity: [
    { value: 'none', label: 'None', tone: 'success' },
    { value: 'minor', label: 'Minor', tone: 'warning' },
    { value: 'major', label: 'Major', tone: 'error' },
  ],
  yesno: [
    { value: 'no', label: 'No', tone: 'success' },
    { value: 'yes', label: 'Yes', tone: 'warning' },
  ],
} as const satisfies Record<string, InspectionScaleOption[]>

export type InspectionScaleKey = keyof typeof INSPECTION_SCALES
export const INSPECTION_SCALE_KEYS = Object.keys(INSPECTION_SCALES) as InspectionScaleKey[]
export const inspectionScaleKeySchema = z.enum(['condition', 'severity', 'yesno'])

export const INSPECTION_SCALE_LABELS: Record<InspectionScaleKey, string> = {
  condition: 'Condition (Good / Fair / Poor / N-A)',
  severity: 'Severity (None / Minor / Major)',
  yesno: 'Checkbox (Yes / No)',
}

/** The option (label + tone) for a status value under a given scale, or undefined if it isn't rated yet. */
export function inspectionScaleOption(
  scaleKey: InspectionScaleKey,
  value: string,
): InspectionScaleOption | undefined {
  return (INSPECTION_SCALES[scaleKey] as readonly InspectionScaleOption[]).find((o) => o.value === value)
}

/** The four angles a whole-vehicle overview photo can be taken from. Fixed — not part of the editable checklist. */
export const INSPECTION_OVERVIEW_ANGLES = [
  { key: 'front', label: 'Front' },
  { key: 'left', label: 'Left side' },
  { key: 'right', label: 'Right side' },
  { key: 'rear', label: 'Rear' },
] as const
export type InspectionOverviewAngle = (typeof INSPECTION_OVERVIEW_ANGLES)[number]['key']

export function isInspectionOverviewAngle(value: string): value is InspectionOverviewAngle {
  return INSPECTION_OVERVIEW_ANGLES.some((a) => a.key === value)
}

// ---------------------------------------------------------------------------
// The editable checklist template — sections and items, stored in the
// database (inspection_template_nodes) and managed from Settings. The shape
// below is what GET /inspection-template returns: a rack-and-level tree,
// same idea as Storage.
// ---------------------------------------------------------------------------

export const INSPECTION_TEMPLATE_NODE_KINDS = ['section', 'item'] as const
export type InspectionTemplateNodeKind = (typeof INSPECTION_TEMPLATE_NODE_KINDS)[number]
export const inspectionTemplateNodeKindSchema = z.enum(INSPECTION_TEMPLATE_NODE_KINDS)

const inspectionTemplateNodeFields = {
  label: text(120),
  /** Ignored for sections. Null (or omitted) defaults an item to 'condition'. */
  scale: inspectionScaleKeySchema.nullable().default(null),
  position: z.number().int().min(0).default(0),
}

export const inspectionTemplateNodeInputSchema = z.object(inspectionTemplateNodeFields)
export type InspectionTemplateNodeInput = z.infer<typeof inspectionTemplateNodeInputSchema>

export const inspectionTemplateNodeSchema = z.object({
  ...inspectionTemplateNodeFields,
  id: idSchema,
  kind: inspectionTemplateNodeKindSchema,
  parentId: idSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type InspectionTemplateNode = z.infer<typeof inspectionTemplateNodeSchema>

/** An item, as it appears nested inside GET /inspection-template. */
export interface InspectionTemplateItem extends InspectionTemplateNode {
  kind: 'item'
}

/** A section, with its items nested — the shape GET /inspection-template returns. */
export interface InspectionTemplateSection extends InspectionTemplateNode {
  kind: 'section'
  items: InspectionTemplateItem[]
}

export function inspectionTemplateItemScale(item: Pick<InspectionTemplateNode, 'scale'>): InspectionScaleKey {
  return item.scale ?? 'condition'
}

// ---------------------------------------------------------------------------
// The factory-default checklist. Used only to seed inspection_template_nodes
// the first time it's read on a fresh install — after that, the database is
// the source of truth and this constant is never consulted again. Not the
// live template: see InspectionTemplateSection/Item above for that.
// ---------------------------------------------------------------------------

interface InspectionSeedItem {
  label: string
  scale?: InspectionScaleKey
}
interface InspectionSeedSection {
  label: string
  items: InspectionSeedItem[]
}

export const DEFAULT_INSPECTION_TEMPLATE: InspectionSeedSection[] = [
  {
    label: 'Body & Paint',
    items: [
      { label: 'Paint condition' },
      { label: 'Scratches / scuffs', scale: 'severity' },
      { label: 'Dents', scale: 'severity' },
      { label: 'Rust / corrosion', scale: 'severity' },
      { label: 'Panel gaps / alignment' },
      { label: 'Evidence of previous repairs', scale: 'yesno' },
    ],
  },
  {
    label: 'Engine',
    items: [
      { label: 'Oil level & condition' },
      { label: 'Coolant level & condition' },
      { label: 'Belts' },
      { label: 'Hoses' },
      { label: 'Leaks' },
      { label: 'Engine mounts' },
      { label: 'Air filter' },
    ],
  },
  {
    label: 'Suspension — Front',
    items: [
      { label: 'Shock absorbers / struts' },
      { label: 'Springs' },
      { label: 'Bushings' },
      { label: 'Ball joints' },
      { label: 'Control arms' },
    ],
  },
  {
    label: 'Suspension — Rear',
    items: [
      { label: 'Shock absorbers / struts' },
      { label: 'Springs' },
      { label: 'Bushings' },
      { label: 'Ball joints' },
      { label: 'Control arms' },
    ],
  },
  {
    label: 'Brakes — Front',
    items: [
      { label: 'Pads' },
      { label: 'Discs / rotors' },
      { label: 'Calipers' },
      { label: 'Brake lines & hoses' },
    ],
  },
  {
    label: 'Brakes — Rear',
    items: [
      { label: 'Pads / shoes' },
      { label: 'Discs / drums' },
      { label: 'Calipers / wheel cylinders' },
      { label: 'Handbrake' },
    ],
  },
  {
    label: 'Electrical',
    items: [
      { label: 'Battery condition' },
      { label: 'Charging system (alternator)' },
      { label: 'Starter motor' },
      { label: 'OBD fault codes' },
      { label: 'Lights (all)' },
      { label: 'Wiring / connectors' },
    ],
  },
  {
    label: 'Steering',
    items: [{ label: 'Steering rack' }, { label: 'Play / free movement' }, { label: 'Wheel alignment' }],
  },
  {
    label: 'Tires & Wheels',
    items: [
      { label: 'Tread depth' },
      { label: 'Tire pressure' },
      { label: 'Sidewall condition' },
      { label: 'Wheel / rim condition' },
    ],
  },
  {
    label: 'Exhaust System',
    items: [{ label: 'Manifold' }, { label: 'Muffler / silencer' }, { label: 'Leaks' }, { label: 'Mounts' }],
  },
  {
    label: 'Fluids',
    items: [
      { label: 'Brake fluid' },
      { label: 'Transmission fluid' },
      { label: 'Power steering fluid' },
      { label: 'Washer fluid' },
    ],
  },
  {
    label: 'Interior',
    items: [
      { label: 'Seats & upholstery' },
      { label: 'Dashboard warning lights' },
      { label: 'AC & heater' },
      { label: 'Seatbelts' },
      { label: 'Infotainment' },
    ],
  },
  {
    label: 'Glass, Mirrors & Wipers',
    items: [{ label: 'Windshield' }, { label: 'Windows' }, { label: 'Mirrors' }, { label: 'Wiper blades' }],
  },
  {
    label: 'Underbody & Drivetrain',
    items: [
      { label: 'Frame / chassis condition' },
      { label: 'Rust / corrosion', scale: 'severity' },
      { label: 'Drive shaft / CV boots' },
      { label: 'Transmission / differential leaks' },
    ],
  },
  {
    label: 'Safety & Documents',
    items: [
      { label: 'Spare tire' },
      { label: 'Jack & tools' },
      { label: 'Fire extinguisher' },
      { label: 'First aid kit / warning triangle' },
    ],
  },
]

// ---------------------------------------------------------------------------
// One saved inspection: ratings/comments/photos against whatever the
// template looked like at the time. Items and section notes are keyed by
// the template node's id, not by name, so renaming a section or item in
// Settings doesn't orphan anything already recorded against it.
// ---------------------------------------------------------------------------

/** What the client sends back for one item: a rating plus an optional comment. The photo is set through its own upload endpoint, never here. */
const inspectionItemInputSchema = z.object({
  status: z.string().min(1).max(20),
  comment: optionalText(500),
})
export type InspectionItemInput = z.infer<typeof inspectionItemInputSchema>

/** What the server stores and returns for one item: the same rating, plus the photo path it owns. */
const inspectionItemStateSchema = inspectionItemInputSchema.extend({
  photoPath: z.string().nullable().default(null),
})
export type InspectionItemState = z.infer<typeof inspectionItemStateSchema>

export const inspectionUpdateSchema = z.object({
  items: z.record(z.string(), inspectionItemInputSchema).default({}),
  sectionNotes: z.record(z.string(), optionalText(2000)).default({}),
  performedAt: optionalText(40),
  performedBy: optionalText(120),
  overallNotes: optionalText(2000),
})
export type InspectionUpdate = z.infer<typeof inspectionUpdateSchema>

export const inspectionSchema = z.object({
  id: idSchema,
  jobcardId: idSchema,
  items: z.record(z.string(), inspectionItemStateSchema),
  sectionNotes: z.record(z.string(), z.string().nullable()),
  /** Angle -> uploaded photo path, or absent/null when nothing's been taken yet. */
  overviewPhotos: z.record(z.string(), z.string().nullable()),
  performedAt: z.string().nullable(),
  performedBy: z.string().nullable(),
  overallNotes: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type Inspection = z.infer<typeof inspectionSchema>
